import { memo, useRef, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  styled,
  Avatar,
  Button,
} from "@mui/material";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import moment from "moment";
import { useLocation } from "react-router-dom";
import {
  ThumbUpOutlined,
  ThumbUp,
  ThumbDown,
  ThumbDownOutlined,
  FileCopyOutlined,
  EmailOutlined,
  IosShare,
  Star,
  StarBorder,
} from "@mui/icons-material";

import remarkBreaks from "remark-breaks";

import ChatLoading from "../ChatLoading/ChatLoading";
import GenAIChat from "../../assets/images/GenAIChat.svg";
import TileIcon from "../../assets/images/TileIcon.svg";
import TileIconPink from "../../assets/images/TileIconPink.svg";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  inputUpdate,
  messageUpdate,
  disableFormContol,
  productsUpdate,
  keyMessageUpdate,
  emailDataUpdate,
} from "../../redux/slice/snackbar";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { getCurrentDateTime } from "../MessageConversionFormat";
import {
  addCustomFavoriteThunk,
  removeFavoriteById,
} from "../../redux/slice/promptLibrary";
import TextField from "@mui/material/TextField";
import VolumeDownOutlinedIcon from "@mui/icons-material/VolumeDownOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import { franc } from "franc-min";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import EmailDialog from "../EmailDialog/EmailDialog";
import { exportToExcelApi } from "../../redux/slice/export-to-excel";
import {
  DEFAULT_TILES,
  RBD_TILES,
  LEADERSHIP_TILES,
} from "../../utils/constantFiles/constant";
import {
  getTrainingCompassPromptsForRole,
  getExternalSourcesPromptsForRole,
  TRAINING_COMPASS_TOOL_ID,
  EXTERNAL_SOURCES_TOOL_ID,
} from "../../sampledata/PromptDialogData";

import { useDisplayIdentity } from "../../hooks/useDisplayIdentity";
import { UserAvatarSkeleton } from "../UserIdentitySkeleton/UserIdentitySkeleton";
import GoalsDataDisclaimer from "../MyPerformance/widgets/GoalsDataDisclaimer";

const IconButtonStyles = styled(IconButton)(() => ({
  color: "#B5BBB6",
  "& svg": {
    color: "#8C8C8C",
    width: "16px",
    height: "16px",
    "&:hover": {
      color: "#6D94A7",
    },
    "&:hover .filled": {
      opacity: 0.3,
      color: "#6D94A7",
    },
  },
}));
const IconButtonActive = styled(IconButton)(() => ({
  color: "#6D94A7",
  "& svg": {
    width: "16px",
    height: "16px",
  },
  "&.Mui-disabled": {
    color: "#474747",
    "& svg": {
      color: "#474747",
      width: "16px",
      height: "16px",
    },
  },
}));

/**
 * Single source of truth for the typewriter reveal rate. ChatFlow.js imports
 * this to compute how long it should wait (for a "final"-only response with
 * no incremental chunks) before swapping the typing bubble for the
 * permanent message, so the two stay in sync if this rate ever changes.
 */
export const TYPEWRITER_CONFIG = { charsPerTick: 12, tickMs: 20 };

/**
 * Always-visible streaming:
 * Reveal text gradually, even if backend sends it instantly.
 */
function useTypewriter(fullText, { charsPerTick = 10, tickMs = 25 } = {}) {
  const [shown, setShown] = useState("");
  const idxRef = useRef(0);
  const fullRef = useRef(fullText || "");

  // Cheap: just keep the ref pointed at the latest text. This runs on every
  // incoming SSE chunk but does NOT touch the interval below, so a burst of
  // fast chunks can't keep tearing down/restarting the ticker (which was
  // the reason nothing appeared until the stream had already finished).
  useEffect(() => {
    const next = fullText || "";

    // New message started (reset condition)
    if (next.length < idxRef.current) {
      idxRef.current = 0;
      setShown("");
    }

    fullRef.current = next;
  }, [fullText]);

  // Runs once and keeps ticking for the lifetime of the component, always
  // reading the latest text off the ref — so the reveal rate is independent
  // of how often (or how fast) new chunks arrive.
  useEffect(() => {
    const id = setInterval(() => {
      const target = fullRef.current || "";
      if (idxRef.current >= target.length) return;

      idxRef.current = Math.min(idxRef.current + charsPerTick, target.length);
      setShown(target.slice(0, idxRef.current));
    }, tickMs);

    return () => clearInterval(id);
  }, [charsPerTick, tickMs]);

  return shown;
}

function ChatsBox({
  messagesEndRef,
  messagesEndRefChild,
  handleInputValue,
  handleLikeDialogClickOpen,
  handleUnLikeDialogClickOpen,
  streamData,
  handleHitlAutoSend,
}) {
  const dispatch = useDispatch();
  const userChatLoading = useSelector((state) => state.newChat.loading);
  const loading = useSelector((state) => state.userChat.isLoading);
  const messages = useSelector((state) => state.snackbar.messages);
  const favorites = useSelector((state) => state.promptLibrary.favorites);
  const formControlDisabled = useSelector(
    (state) => state.snackbar.formControlDisabled,
  );
  const products = useSelector((state) => state.snackbar.product);
  const keyMessages = useSelector((state) => state.snackbar.keyMessages);
  const streamLoading = useSelector((state) => state.stream.loading);
  const newstreamLoading = useSelector((state) => state.newChatStream.loading);
  const kbStreamLoading = useSelector(
    (state) => state.knowledgeBotStream.loading,
  );
  const location = useLocation();
  const authUser = useSelector((state) => state.auth.user);
  const role = authUser?.business_function || "";
  const isRBD = (role ?? "").trim().toUpperCase() === "RBD";
  const isLeadership = (role ?? "").trim().toUpperCase() === "LEADERSHIP";
  const activeToolId = useSelector((state) => state.snackbar.activeChatToolId);

  const { initials, isLoading: displayIdentityLoading } = useDisplayIdentity();
  const speechConfigAzure = useSelector(
    (state) => state.speechConfig.speechConfig,
  );

  const [copied, setCopied] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const synthesizerRef = useRef(null);
  const [audioInstance, setAudioInstance] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const questionsEndRef = useRef(null);

  useEffect(() => {
    if (messages?.length > 0 && questionsEndRef.current) {
      questionsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Build full stream text
  const fullStreamText = useMemo(() => {
    if (!Array.isArray(streamData)) return "";
    return streamData.join("");
  }, [streamData]);

  // Reveal slowly (this is what makes streaming visible)
  const typedStreamText = useTypewriter(fullStreamText, TYPEWRITER_CONFIG);

  // HITL lock logic:
  // A HITL clarification message is "resolved" if a USER message exists after it.
  const hitlResolvedByIndex = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) return {};

    const resolvedMap = {};
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (m?.agentType !== "hitl_clarification") continue;

      let hasUserAfter = false;
      for (let j = i + 1; j < messages.length; j++) {
        if (
          messages[j]?.sender === "user" &&
          (messages[j]?.text || "").trim()
        ) {
          hasUserAfter = true;
          break;
        }
      }
      resolvedMap[i] = hasUserAfter;
    }

    return resolvedMap;
  }, [messages]);

  const lastBotMessageIndex = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) return -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.sender === "bot") return i;
    }
    return -1;
  }, [messages]);

  // The user prompt tied to a response bubble = the nearest preceding user
  // message. Chat-fired prompts are freeform text with no prompt_id, so the
  // star here goes through the custom-favorite endpoint, and favorite state
  // is matched on the exact prompt text in state.favorites.
  const getPromptForBotIndex = (idx) => {
    for (let i = idx - 1; i >= 0; i--) {
      if (messages?.[i]?.sender === "user") return messages[i]?.text || "";
    }
    return "";
  };

  const findFavouriteByText = (text) => {
    const target = (text || "").trim();
    if (!target) return null;
    return favorites.find((f) => (f.prompt || "").trim() === target) || null;
  };

  const toggleChatFavourite = (text) => {
    const target = (text || "").trim();
    if (!target) return;
    const existing = findFavouriteByText(target);
    if (existing) {
      dispatch(removeFavoriteById(existing.favorite_id));
    } else {
      dispatch(addCustomFavoriteThunk(target));
    }
  };

  const handleToggleSpeech = (text, index) => {
    if (speakingIndex === index) {
      if (audioInstance) {
        audioInstance.pause();
        audioInstance.src = "";
      }
      if (synthesizerRef.current) {
        synthesizerRef.current.close();
        synthesizerRef.current = null;
      }
      setSpeakingIndex(null);
      return;
    }

    let detected = franc(text, {
      whitelist: ["hin", "guj", "tam", "tel", "kan", "mar", "ben", "eng"],
    });
    if (detected === "bho") detected = "hin";

    const langToVoiceMap = {
      hin: "hi-IN-SwaraNeural",
      guj: "gu-IN-DhwaniNeural",
      tam: "ta-IN-PallaviNeural",
      tel: "te-IN-MohanNeural",
      kan: "kn-IN-GaganNeural",
      mar: "mr-IN-AarohiNeural",
      ben: "bn-IN-TanishaaNeural",
      eng: "en-US-JennyNeural",
      und: "en-US-JennyNeural",
    };

    const voiceName = langToVoiceMap[detected] || "en-US-JennyNeural";

    const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
      speechConfigAzure.speech_key,
      speechConfigAzure.speech_region,
    );
    speechConfig.speechSynthesisVoiceName = voiceName;

    const stream = SpeechSDK.AudioOutputStream.createPullStream();
    const audioConfig = SpeechSDK.AudioConfig.fromStreamOutput(stream);
    const synthesizer = new SpeechSDK.SpeechSynthesizer(
      speechConfig,
      audioConfig,
    );

    synthesizerRef.current = synthesizer;
    setSpeakingIndex(index);

    synthesizer.speakTextAsync(
      text,
      (result) => {
        const audioData = result.audioData;
        const blob = new Blob([audioData], { type: "audio/wav" });
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);

        audio.play();
        setAudioInstance(audio);

        audio.onended = () => {
          setSpeakingIndex(null);
          synthesizer.close();
          synthesizerRef.current = null;
        };
        audio.onerror = () => {
          setSpeakingIndex(null);
          synthesizer.close();
          synthesizerRef.current = null;
        };
      },
      (err) => {
        console.error("Speech synthesis error:", err);
        setSpeakingIndex(null);
        synthesizer.close();
        synthesizerRef.current = null;
      },
    );
  };

  const handleProductsChange = (event) => {
    dispatch(productsUpdate(event.target.value));
  };

  const handleKeyMessagesChange = (event) => {
    dispatch(keyMessageUpdate(event.target.value));
  };

  const handleCopyToClipboard = (text) => {
    const value = text == null ? "" : String(text);
    try {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("Fallback: Copy command was unsuccessful");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
    }
  };

  const handleClickOpen = (mail) => {
    dispatch(emailDataUpdate(mail));
    setEmailOpen(true);
  };
  const handleClose = () => {
    setEmailOpen(false);
  };

  const formatDate = (isoString) => {
    return moment(isoString).format("MM/DD/YYYY hh:mma");
  };

  // ⚠️ NOTE: this runs every render in your current file; keeping it as-is but this is not ideal.
  const links = document.querySelectorAll("a");
  links.forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  const tilefull = useMemo(() => {
    if (activeToolId === TRAINING_COMPASS_TOOL_ID) {
      return getTrainingCompassPromptsForRole(role, 4).map((title) => ({
        title,
      }));
    }

    if (activeToolId === EXTERNAL_SOURCES_TOOL_ID) {
      return getExternalSourcesPromptsForRole(role, 4).map((title) => ({
        title,
      }));
    }

    return isLeadership ? LEADERSHIP_TILES : isRBD ? RBD_TILES : DEFAULT_TILES;
  }, [activeToolId, role, isLeadership, isRBD]);

  const firstRowCount = 2;
  const secondRowCount = 2;
  const tileone = tilefull.slice(0, firstRowCount);
  const tiletwo = tilefull.slice(firstRowCount, firstRowCount + secondRowCount);

  const handleSubmitButton = () => {
    dispatch(disableFormContol(true));
    dispatch(
      messageUpdate([
        {
          text: "Thank you for providing the response. We have successfully logged your call notes!",
          sender: "bot",
          chatId: "",
          userSessionId: "",
          feedback: "",
          time: getCurrentDateTime(),
          options: [],
          email: "",
          withoutMarkDown: "",
          chartIntent: "",
          imgPath: "",
          tableFound: "",
          blobName: "",
        },
      ]),
    );
  };

  const handleExportToExcel = async (fileName, sessionId) => {
    const payload = {
      sessionId: sessionId,
      data: {
        excel_file_name: fileName,
      },
    };
    const result = await dispatch(exportToExcelApi(payload));

    if (exportToExcelApi.fulfilled.match(result)) {
      const blob = result.payload;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(link);
    } else {
      console.error("export failed", result.payload);
    }
  };

  return (
    <Box
      flex={1}
      overflow="auto"
      ref={messagesEndRef}
      className="chat-panel-parent"
      style={{
        paddingBottom: "10px",
        width: "100%",
        marginTop: "10px",
        marginBottom: "20px",
      }}
    >
      {messages.length > 0 ? (
        <Stack spacing={2} className="chat-box-stack-holder">
          {messages?.map((msg, index) => (
            <Box
              key={index}
              className={
                msg?.sender === "user" ? "chat-box-user" : "chat-box-bot"
              }
              style={{
                alignSelf: msg?.sender === "user" ? "flex-end" : "flex-start",
              }}
            >
              <Stack spacing={2} direction="row" alignItems="center" p={1}>
                {msg?.sender !== "user" && (
                  <img
                    src={GenAIChat}
                    alt="logo"
                    className="lupin-logo-bot"
                    style={{
                      alignSelf: "baseline",
                      background: "#6D94A7",
                      borderRadius: "100%",
                    }}
                  />
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {msg?.sender !== "user" &&
                    msg?.agentType &&
                    msg.agentType !== "hitl_clarification" && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.25,
                          py: 1.25,
                          mb: 0.75,
                          borderRadius: "4px",
                          bgcolor: "#FFFFFF",
                          color: "#396D87",
                          fontWeight: 600,
                          fontSize: 16,
                          alignSelf: "flex-start",
                          boxShadow: "rgba(0, 0, 0, 0.2) 0px 1px 2px 0px",
                        }}
                      >
                        {msg?.agentType}
                      </Box>
                    )}
                  <Box>
                    {msg?.text !== "" && (
                      <Paper
                        className="bot-page-content-display"
                        style={{
                          display: msg?.text === "" && "none",
                          boxShadow:
                            msg?.sender === "user"
                              ? "none"
                              : "rgba(0, 0, 0, 0.2) 0px 1px 2px 0px;",
                        }}
                      >
                        {msg?.text !== "bottext" ? (
                          <>
                            <Markdown
                              className="markdown-text"
                              rehypePlugins={[rehypeRaw]}
                              remarkPlugins={[remarkGfm]}
                            >
                              {msg?.text}
                            </Markdown>

                            {msg?.sender === "bot" && msg?.chartIntent && (
                              <img
                                src={`data:image/png;base64,${msg?.imgPath}`}
                                alt="no-chart-image"
                                style={{
                                  width: "80%",
                                  height: "auto",
                                  borderRadius: "8px",
                                  marginTop: "8px",
                                  objectFit: "contain",
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <div>
                            <p>
                              To log the notes from your recent call with{" "}
                              <strong>Dr. ADAM BOSAK</strong>. I'll need some
                              specific details about the discussion. Could you
                              please provide the following information
                            </p>
                            <br />
                            <div className="products-holder-bot">
                              <strong>
                                Which product(s) did you detail or discuss with
                                Dr. ADAM BOSAK?{" "}
                              </strong>{" "}
                              <Box sx={{ minWidth: 120, pl: 1 }}>
                                <FormControl fullWidth>
                                  <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={products}
                                    onChange={handleProductsChange}
                                    disabled={formControlDisabled}
                                  >
                                    <MenuItem value="TVSASO DPI">
                                      TVSASO DPI
                                    </MenuItem>
                                    <MenuItem value="TYVASO PAH">
                                      TYVASO PAH
                                    </MenuItem>
                                    <MenuItem value="ORENITRAM">
                                      ORENITRAM
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                            </div>
                            <div className="products-holder-bot">
                              <strong>
                                What were the key messages or benefits you
                                highlighted regarding the product(s)?{" "}
                              </strong>
                              <Box sx={{ minWidth: 200, pl: 1 }}>
                                <FormControl fullWidth>
                                  <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={keyMessages}
                                    disabled={formControlDisabled}
                                    onChange={handleKeyMessagesChange}
                                  >
                                    <MenuItem value={10}>
                                      Patient had an 11.5m additional increase
                                      in 6MWD after transitioning to TYVASO DPI
                                    </MenuItem>
                                    <MenuItem value={20}>
                                      TYVASO DPI is dosed with just 1 breath per
                                      cartidge, 4x daily
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                            </div>
                            <div>
                              <br />
                              <div className="hardcoded-optional-suggestions">
                                Did Dr. ADAM BOSAK give any suggestions,
                                objections, or feedback about the product(s)?
                              </div>
                              <TextField
                                id="outlined-multiline-static"
                                className="hardcoded-text-field"
                                multiline
                                rows={2}
                                disabled={formControlDisabled}
                              />
                              <div className="hardcoded-optional-suggestions">
                                Did you schedule a follow-up, and is there
                                anything specific to prepare for the next visit?
                              </div>
                              <TextField
                                id="outlined-multiline-static"
                                className="hardcoded-text-field"
                                multiline
                                rows={2}
                                disabled={formControlDisabled}
                              />
                            </div>
                            <Button
                              className="dropdown-submitbutton"
                              disabled={
                                !keyMessages || !products || formControlDisabled
                              }
                              onClick={handleSubmitButton}
                            >
                              Submit
                            </Button>
                          </div>
                        )}

                        {/* ---------- HITL recommendations / entity disambiguation ---------- */}
                        {msg?.agentType === "hitl_clarification" &&
                          (() => {
                            const isResolved = !!hitlResolvedByIndex[index];
                            const hitlType = msg?.hitlType || "generic";
                            const hitlStep = msg?.hitlStep || 1;
                            const hitlCase = (
                              msg?.hitlCase || ""
                            ).toLowerCase();
                            const sections = msg?.sections || [];
                            const selectedType = msg?.selectedType || null;
                            const recommendations = msg?.recommendations || {};
                            const proposed = msg?.proposed || null;
                            const hcpOptions =
                              msg?.hcpOptions || recommendations?.hcp || [];
                            const hcoOptions =
                              msg?.hcoOptions || recommendations?.hco || [];

                            // Legacy case detection from sections
                            const legacyCase = (
                              (sections[0] || {}).case || ""
                            ).toUpperCase();
                            const primaryType =
                              (sections[0] || {}).entity_type || "hcp";

                            // Helper: render a single candidate card with context fields
                            const renderCandidateCard = (
                              r,
                              globalIdx,
                              entityType,
                            ) => {
                              const contextParts = [];

                              // Show extra context only for HCPs
                              if (entityType === "hcp") {
                                if (r.associated_entity)
                                  contextParts.push(r.associated_entity);
                                if (r.group) contextParts.push(r.group);
                                if (!r.associated_entity && r.territory)
                                  contextParts.push(r.territory);
                              }

                              // For HCOs, do not show associated_entity or group
                              // If later needed, you can selectively show only territory here.

                              const contextText = contextParts.join(" | ");

                              return (
                                <Box
                                  key={globalIdx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    border: "1px solid #e0e0e0",
                                    opacity: isResolved ? 0.5 : 1,
                                    cursor: isResolved
                                      ? "not-allowed"
                                      : "pointer",
                                    pointerEvents: isResolved ? "none" : "auto",
                                    "&:hover": {
                                      backgroundColor: isResolved
                                        ? "transparent"
                                        : "#f0f7ff",
                                      borderColor: isResolved
                                        ? "#e0e0e0"
                                        : "#6D94A7",
                                    },
                                  }}
                                  onClick={() => {
                                    if (isResolved) return;
                                    handleHitlAutoSend(
                                      String(globalIdx),
                                      r.name,
                                    );
                                  }}
                                  title={
                                    isResolved ? "Resolved" : `Select ${r.name}`
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.25,
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: "#6D94A7",
                                          minWidth: 24,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {globalIdx}.
                                      </Typography>
                                      <Typography
                                        sx={{ fontSize: 14, fontWeight: 500 }}
                                        noWrap
                                      >
                                        {r.name}
                                      </Typography>
                                    </Box>
                                    {contextText && (
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: "#888",
                                          pl: "32px",
                                        }}
                                        noWrap
                                      >
                                        {contextText}
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    disabled={isResolved}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isResolved) return;
                                      if (navigator.clipboard?.writeText) {
                                        navigator.clipboard.writeText(r.name);
                                      }
                                      handleHitlAutoSend(r.name, r.name);
                                    }}
                                    title={
                                      isResolved ? "Resolved" : "Copy name"
                                    }
                                  >
                                    <FileCopyOutlined
                                      sx={{ fontSize: 16, color: "#666" }}
                                    />
                                  </IconButton>
                                </Box>
                              );
                            };

                            // Helper: render a single-type candidate list
                            const renderCandidateList = (
                              candidates,
                              typeLabel,
                            ) => (
                              <Box sx={{ mt: 1, width: "100%" }}>
                                {typeLabel && (
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: "#6b6b6b",
                                      mb: 0.5,
                                    }}
                                  >
                                    Top matches
                                    {typeLabel ? ` — ${typeLabel}` : ""}
                                  </Typography>
                                )}
                                <Stack
                                  spacing={0.75}
                                  sx={{
                                    maxHeight: 220,
                                    overflowY: "auto",
                                    pr: 1,
                                  }}
                                >
                                  {candidates.map((r, i) =>
                                    renderCandidateCard(r, i + 1, primaryType),
                                  )}
                                  {candidates.length === 0 && (
                                    <Typography
                                      sx={{ fontSize: 13, color: "#9a9a9a" }}
                                    >
                                      No matches found. Please type the full
                                      name.
                                    </Typography>
                                  )}
                                </Stack>
                                {!isResolved && candidates.length > 0 && (
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#888",
                                      mt: 0.75,
                                    }}
                                  >
                                    Click a match above, type a number (1-
                                    {candidates.length}), or enter the full
                                    name.
                                  </Typography>
                                )}
                              </Box>
                            );

                            // ====================================================
                            // NEW: CONFIRM case — Yes/No buttons
                            // "Did you mean X? [Yes] [No]"
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlCase === "confirm"
                            ) {
                              return (
                                <Box sx={{ mt: 1.5, width: "100%" }}>
                                  <Stack direction="row" spacing={1.5}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disabled={isResolved}
                                      onClick={() => {
                                        if (isResolved) return;
                                        handleHitlAutoSend("Yes", "Yes");
                                      }}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: 14,
                                        fontWeight: 600,
                                        backgroundColor: "#6D94A7",
                                        color: "#fff",
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: 1,
                                        opacity: isResolved ? 0.5 : 1,
                                        "&:hover": {
                                          backgroundColor: "#396D87",
                                        },
                                        "&.Mui-disabled": {
                                          backgroundColor: "#E5E5E5",
                                          color: "#aaa",
                                        },
                                      }}
                                    >
                                      Yes
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={isResolved}
                                      onClick={() => {
                                        if (isResolved) return;
                                        handleHitlAutoSend("No", "No");
                                      }}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: 14,
                                        fontWeight: 600,
                                        borderColor: "#6D94A7",
                                        color: "#6D94A7",
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: 1,
                                        opacity: isResolved ? 0.5 : 1,
                                        "&:hover": {
                                          backgroundColor: "#F3FBFF",
                                          borderColor: "#396D87",
                                        },
                                      }}
                                    >
                                      No
                                    </Button>
                                  </Stack>
                                  {!isResolved && (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#888",
                                        mt: 0.75,
                                      }}
                                    >
                                      Click a button above or type Yes / No in
                                      the input.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }

                            // ====================================================
                            // NEW: SELECT case — Combined HCP + HCO numbered list
                            // Shows top-5 HCP (1-5) + top-5 HCO (6-10)
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlCase === "select"
                            ) {
                              const nHcp = hcpOptions.length;
                              const total = nHcp + hcoOptions.length;

                              return (
                                <Box sx={{ mt: 1, width: "100%" }}>
                                  <Stack
                                    spacing={0.75}
                                    sx={{
                                      maxHeight: 340,
                                      overflowY: "auto",
                                      pr: 1,
                                    }}
                                  >
                                    {hcpOptions.length > 0 && (
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#396D87",
                                          mt: 0.5,
                                          mb: 0.25,
                                        }}
                                      >
                                        Healthcare Professionals (HCP)
                                      </Typography>
                                    )}
                                    {hcpOptions.map((r, i) =>
                                      renderCandidateCard(r, i + 1, "hcp"),
                                    )}

                                    {hcoOptions.length > 0 && (
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#396D87",
                                          mt: 1,
                                          mb: 0.25,
                                        }}
                                      >
                                        Healthcare Organizations (HCO)
                                      </Typography>
                                    )}
                                    {hcoOptions.map((r, i) =>
                                      renderCandidateCard(
                                        r,
                                        nHcp + i + 1,
                                        "hco",
                                      ),
                                    )}

                                    {total === 0 && (
                                      <Typography
                                        sx={{ fontSize: 13, color: "#9a9a9a" }}
                                      >
                                        No matches found. Please type the full
                                        name below.
                                      </Typography>
                                    )}
                                  </Stack>
                                  {!isResolved && total > 0 && (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#888",
                                        mt: 0.75,
                                      }}
                                    >
                                      Select a number (1-{total}), or type the{" "}
                                      <strong>full name</strong> if none of
                                      these match.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }

                            // ====================================================
                            // NEW: NO_MATCH case — Free text prompt
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlCase === "no_match"
                            ) {
                              return (
                                <Box sx={{ mt: 1, width: "100%" }}>
                                  {!isResolved && (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#888",
                                        mt: 0.5,
                                      }}
                                    >
                                      Type the complete full name in the input
                                      below (e.g., "Dr. Daniel Brown" or "Brown
                                      Memorial Hospital").
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }

                            // ====================================================
                            // LEGACY: Case B, Step 1: Type Selection Buttons
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlStep === 1 &&
                              legacyCase === "B"
                            ) {
                              return (
                                <Box sx={{ mt: 1.5, width: "100%" }}>
                                  <Stack direction="row" spacing={1.5}>
                                    {["hcp", "hco"].map((etype) => {
                                      const label =
                                        etype === "hcp"
                                          ? "Healthcare Professional (HCP)"
                                          : "Healthcare Organization (HCO)";
                                      const shortLabel = etype.toUpperCase();
                                      return (
                                        <Button
                                          key={etype}
                                          variant="outlined"
                                          size="small"
                                          disabled={isResolved}
                                          onClick={() => {
                                            if (isResolved) return;
                                            handleHitlAutoSend(
                                              shortLabel,
                                              label,
                                            );
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            fontSize: 13,
                                            fontWeight: 500,
                                            borderColor: "#6D94A7",
                                            color: "#6D94A7",
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 1,
                                            opacity: isResolved ? 0.5 : 1,
                                            "&:hover": {
                                              backgroundColor: "#F3FBFF",
                                              borderColor: "#396D87",
                                            },
                                          }}
                                        >
                                          {label}
                                        </Button>
                                      );
                                    })}
                                  </Stack>
                                  {!isResolved && (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#888",
                                        mt: 0.75,
                                      }}
                                    >
                                      Click a button above or type HCP / HCO in
                                      the input.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }

                            // ====================================================
                            // LEGACY: Case C — Low-confidence candidate list
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlStep === 1 &&
                              legacyCase === "C"
                            ) {
                              const candidates =
                                recommendations[primaryType] || [];
                              const typeLabel =
                                primaryType === "hcp"
                                  ? "Healthcare Professional (HCP)"
                                  : primaryType === "hco"
                                    ? "Healthcare Organization (HCO)"
                                    : primaryType.toUpperCase();
                              return renderCandidateList(candidates, typeLabel);
                            }

                            // ====================================================
                            // LEGACY: Case D — No match free text
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlStep === 1 &&
                              legacyCase === "D"
                            ) {
                              return (
                                <Box sx={{ mt: 1, width: "100%" }}>
                                  {!isResolved && (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#888",
                                        mt: 0.5,
                                      }}
                                    >
                                      Type the complete full name in the input
                                      below.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }

                            // ====================================================
                            // LEGACY: Case B Step 2 / generic step 2
                            // ====================================================
                            if (
                              hitlType === "entity_disambiguation" &&
                              hitlStep === 2 &&
                              selectedType
                            ) {
                              const candidates =
                                recommendations[selectedType] || [];
                              const typeLabel =
                                selectedType === "hcp"
                                  ? "Healthcare Professional (HCP)"
                                  : "Healthcare Organization (HCO)";
                              return renderCandidateList(candidates, typeLabel);
                            }

                            // ====================================================
                            // Fallback: entity_disambiguation with unknown case
                            // ====================================================
                            if (hitlType === "entity_disambiguation") {
                              // If we have both HCP and HCO options, render combined list
                              if (
                                hcpOptions.length > 0 ||
                                hcoOptions.length > 0
                              ) {
                                const nHcp = hcpOptions.length;
                                const total = nHcp + hcoOptions.length;
                                return (
                                  <Box sx={{ mt: 1, width: "100%" }}>
                                    <Stack
                                      spacing={0.75}
                                      sx={{
                                        maxHeight: 340,
                                        overflowY: "auto",
                                        pr: 1,
                                      }}
                                    >
                                      {hcpOptions.length > 0 && (
                                        <Typography
                                          sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: "#396D87",
                                            mb: 0.25,
                                          }}
                                        >
                                          Healthcare Professionals (HCP)
                                        </Typography>
                                      )}
                                      {hcpOptions.map((r, i) =>
                                        renderCandidateCard(r, i + 1, "hcp"),
                                      )}
                                      {hcoOptions.length > 0 && (
                                        <Typography
                                          sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: "#396D87",
                                            mt: 1,
                                            mb: 0.25,
                                          }}
                                        >
                                          Healthcare Organizations (HCO)
                                        </Typography>
                                      )}
                                      {hcoOptions.map((r, i) =>
                                        renderCandidateCard(
                                          r,
                                          nHcp + i + 1,
                                          "hco",
                                        ),
                                      )}
                                    </Stack>
                                    {!isResolved && total > 0 && (
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: "#888",
                                          mt: 0.75,
                                        }}
                                      >
                                        Select a number (1-{total}), or type the
                                        full name.
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }
                              // Single-type recommendations fallback
                              const candidates =
                                recommendations[primaryType] || [];
                              if (candidates.length > 0) {
                                const typeLabel =
                                  primaryType === "hcp"
                                    ? "Healthcare Professional (HCP)"
                                    : primaryType === "hco"
                                      ? "Healthcare Organization (HCO)"
                                      : "";
                                return renderCandidateList(
                                  candidates,
                                  typeLabel,
                                );
                              }
                              return null;
                            }

                            // ====================================================
                            // Generic HITL: Legacy flat recommendations list
                            // ====================================================
                            if (
                              recommendations &&
                              (recommendations.hcp || recommendations.hco)
                            ) {
                              const recs = [];
                              const push = (arr, kind) => {
                                if (!Array.isArray(arr)) return;
                                arr.forEach((r) =>
                                  recs.push({
                                    name: r.name,
                                    score: r.score || 0,
                                    kind,
                                    associated_entity:
                                      r.associated_entity || "",
                                    group: r.group || "",
                                    territory: r.territory || "",
                                  }),
                                );
                              };
                              push(recommendations.hcp, "hcp");
                              push(recommendations.hco, "hco");
                              recs.sort(
                                (a, b) => (b.score || 0) - (a.score || 0),
                              );

                              return (
                                <Box sx={{ mt: 1, width: "100%" }}>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: "#6b6b6b",
                                      mb: 0.5,
                                    }}
                                  >
                                    Suggestions
                                  </Typography>
                                  <Stack
                                    spacing={1}
                                    sx={{
                                      maxHeight: 220,
                                      overflowY: "auto",
                                      pr: 1,
                                    }}
                                  >
                                    {recs.map((r, i) => {
                                      const contextParts = [];
                                      if (r.associated_entity)
                                        contextParts.push(r.associated_entity);
                                      if (r.group) contextParts.push(r.group);
                                      const contextText =
                                        contextParts.join(" | ");

                                      return (
                                        <Box
                                          key={i}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            opacity: isResolved ? 0.5 : 1,
                                            cursor: isResolved
                                              ? "not-allowed"
                                              : "pointer",
                                            pointerEvents: isResolved
                                              ? "none"
                                              : "auto",
                                            "&:hover": {
                                              backgroundColor: isResolved
                                                ? "transparent"
                                                : "#f5f5f5",
                                            },
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 0.25,
                                              flex: 1,
                                              minWidth: 0,
                                              cursor: isResolved
                                                ? "not-allowed"
                                                : "pointer",
                                            }}
                                            onClick={() => {
                                              if (!isResolved)
                                                handleHitlAutoSend(
                                                  r.name,
                                                  r.name,
                                                );
                                            }}
                                            title={
                                              isResolved
                                                ? "Resolved"
                                                : `Click to prefill: ${r.name}`
                                            }
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: 14,
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {r.name}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  color: "#8a8a8a",
                                                }}
                                              >
                                                {r.kind?.toUpperCase()}
                                              </Typography>
                                            </Box>
                                            {contextText && (
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  color: "#888",
                                                }}
                                                noWrap
                                              >
                                                {contextText}
                                              </Typography>
                                            )}
                                          </Box>
                                          <IconButton
                                            size="small"
                                            disabled={isResolved}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isResolved) return;
                                              if (
                                                navigator.clipboard?.writeText
                                              )
                                                navigator.clipboard.writeText(
                                                  r.name,
                                                );
                                              handleHitlAutoSend(
                                                r.name,
                                                r.name,
                                              );
                                            }}
                                            title={
                                              isResolved
                                                ? "Resolved"
                                                : "Copy name"
                                            }
                                          >
                                            <FileCopyOutlined
                                              sx={{
                                                fontSize: 16,
                                                color: "#666",
                                              }}
                                            />
                                          </IconButton>
                                        </Box>
                                      );
                                    })}
                                    {recs.length === 0 && (
                                      <Typography
                                        sx={{ fontSize: 13, color: "#9a9a9a" }}
                                      >
                                        No suggestions found.
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              );
                            }

                            return null;
                          })()}

                        {/* ---------- end HITL recommendations ---------- */}

                        {msg?.sender !== "user" &&
                          msg?.text !== "bottext" &&
                          msg?.text !==
                            "Thank you for providing the response. We have successfully logged your call notes!" && (
                            <Box>
                              {msg?.feedback === true ? (
                                <Tooltip title="Like">
                                  <IconButtonActive
                                    onClick={() =>
                                      handleLikeDialogClickOpen(
                                        msg?.chatId,
                                        msg?.userSessionId,
                                      )
                                    }
                                  >
                                    <ThumbUp disabled />
                                  </IconButtonActive>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Like">
                                  <IconButtonStyles
                                    onClick={() =>
                                      handleLikeDialogClickOpen(
                                        msg?.chatId,
                                        msg?.userSessionId,
                                      )
                                    }
                                  >
                                    <ThumbUpOutlined />
                                  </IconButtonStyles>
                                </Tooltip>
                              )}

                              {msg?.feedback === false ? (
                                <Tooltip
                                  title="Dislike"
                                  onClick={() =>
                                    handleUnLikeDialogClickOpen(
                                      msg?.chatId,
                                      msg?.userSessionId,
                                    )
                                  }
                                >
                                  <IconButtonActive>
                                    <ThumbDown disabled />
                                  </IconButtonActive>
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  title="Dislike"
                                  onClick={() =>
                                    handleUnLikeDialogClickOpen(
                                      msg?.chatId,
                                      msg?.userSessionId,
                                    )
                                  }
                                >
                                  <IconButtonStyles>
                                    <ThumbDownOutlined />
                                  </IconButtonStyles>
                                </Tooltip>
                              )}

                              {(() => {
                                const questionText =
                                  getPromptForBotIndex(index);
                                const isFav = Boolean(
                                  findFavouriteByText(questionText),
                                );
                                return (
                                  <Tooltip title="Add prompt to your favorites">
                                    {isFav ? (
                                      <IconButtonActive
                                        onClick={() =>
                                          toggleChatFavourite(questionText)
                                        }
                                      >
                                        <Star sx={{ color: "#E56800" }} />
                                      </IconButtonActive>
                                    ) : (
                                      <IconButtonStyles
                                        onClick={() =>
                                          toggleChatFavourite(questionText)
                                        }
                                      >
                                        <StarBorder />
                                      </IconButtonStyles>
                                    )}
                                  </Tooltip>
                                );
                              })()}

                              <Tooltip
                                title={copied ? "Copied to clipboard" : "Copy"}
                              >
                                <IconButtonStyles
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      msg?.withoutMarkDown?.trim()
                                        ? msg.withoutMarkDown
                                        : msg?.text,
                                    )
                                  }
                                >
                                  <FileCopyOutlined />
                                </IconButtonStyles>
                              </Tooltip>

                              {msg.email && (
                                <Tooltip title="Share to Email">
                                  <IconButtonStyles
                                    onClick={() => handleClickOpen(msg.email)}
                                  >
                                    <EmailOutlined />
                                  </IconButtonStyles>
                                </Tooltip>
                              )}

                              {msg?.withoutMarkDown && (
                                <IconButtonStyles
                                  onClick={() =>
                                    handleToggleSpeech(
                                      msg?.withoutMarkDown,
                                      index,
                                    )
                                  }
                                >
                                  {speakingIndex === index ? (
                                    <VolumeDownOutlinedIcon />
                                  ) : (
                                    <VolumeOffOutlinedIcon />
                                  )}
                                </IconButtonStyles>
                              )}

                              {msg?.tableFound && (
                                <Tooltip
                                  title="Export"
                                  onClick={() =>
                                    handleExportToExcel(
                                      msg?.blobName,
                                      msg?.userSessionId,
                                    )
                                  }
                                >
                                  <IconButtonStyles>
                                    <IosShare />
                                  </IconButtonStyles>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                      </Paper>
                    )}
                    {msg?.sender === "bot" && msg?.goalsDataDisclaimerShow && (
                      <Box sx={{ mt: 1, width: "100%" }}>
                        <GoalsDataDisclaimer
                          disclaimerText={
                            "Any responses related to Goals show values from the previous quarter, as the goals for the current quarter are not yet available"
                          }
                        />
                      </Box>
                    )}
                    {msg?.sender === "bot" &&
                      index === lastBotMessageIndex &&
                      Array.isArray(msg?.options) &&
                      msg.options.length > 0 && (
                        <Box sx={{ mt: 1.25, width: "100%" }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#6b6b6b",
                              mb: 0.75,
                            }}
                          >
                            Suggested follow-ups
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            className="sender-chip-suggestions"
                          >
                            {msg.options.map((q, i) => (
                              <Chip
                                key={`${index}-sq-${i}`}
                                label={q}
                                clickable
                                onClick={() => handleInputValue(q)}
                                className="chip-parent-holder"
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                  </Box>
                </Box>

                {msg?.sender === "user" &&
                  (displayIdentityLoading ? (
                    <UserAvatarSkeleton
                      size={40}
                      sx={{ alignSelf: "baseline" }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        bgcolor: "#E56800",
                        color: "#FFFFFF",
                        fontWeight: "700",
                        fontSize: "16px",
                        width: "40px",
                        height: "40px",
                        alignSelf: "baseline",
                      }}
                    >
                      {initials}
                    </Avatar>
                  ))}
              </Stack>

              <Typography
                className={
                  msg?.sender === "user"
                    ? "time-format-for-user time-format"
                    : "time-format-for-bot time-format"
                }
              >
                {formatDate(msg?.time)}
              </Typography>
            </Box>
          ))}

          {/* Streaming bubble: now uses SAME Paper class as bot messages (fixes background mismatch) */}
          {typedStreamText && (
            <Box className="chat-box-bot" style={{ alignSelf: "flex-start" }}>
              <Stack spacing={2} direction="row" alignItems="center" p={1}>
                <img
                  src={GenAIChat}
                  alt="logo"
                  className="lupin-logo-bot"
                  style={{
                    alignSelf: "baseline",
                    background: "#6D94A7",
                    borderRadius: "100%",
                  }}
                />

                <Paper
                  className="bot-page-content-display"
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.2) 0px 1px 2px 0px;",
                  }}
                >
                  <Markdown
                    className="markdown-text"
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                  >
                    {typedStreamText}
                  </Markdown>
                </Paper>
              </Stack>
            </Box>
          )}

          <div ref={questionsEndRef}></div>
          <div ref={messagesEndRefChild} />

          {(userChatLoading ||
            loading ||
            streamLoading ||
            newstreamLoading ||
            kbStreamLoading) &&
            streamData.length === 0 && <ChatLoading />}
        </Stack>
      ) : (
        <>
          <Carousel showThumbs={true} className={`sales-rep-copilot--carousal`}>
            <div>
              {tileone.map((section, index) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={index}
                  className="grid-item-for-content"
                >
                  <Paper
                    className="paper-logo-predefined-content"
                    elevation={0}
                    key={index}
                    onClick={() => handleInputValue(section.title)}
                  >
                    <img src={TileIcon} alt="tile-icon" className="tile-icon" />
                    <img
                      src={TileIconPink}
                      alt="tile-icon"
                      className="tile-icon-pink"
                    />
                    <p>{section.title}</p>
                  </Paper>
                </Grid>
              ))}
            </div>
            <div>
              {tiletwo.map((section, index) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={index}
                  className="grid-item-for-content"
                >
                  <Paper
                    className="paper-logo-predefined-content"
                    elevation={0}
                    key={index}
                    onClick={() => handleInputValue(section.title)}
                  >
                    <img src={TileIcon} alt="tile-icon" className="tile-icon" />
                    <img
                      src={TileIconPink}
                      alt="tile-icon"
                      className="tile-icon-pink"
                    />
                    <p>{section.title}</p>
                  </Paper>
                </Grid>
              ))}
            </div>
          </Carousel>

          <Grid container spacing={2} mx="auto" className="gridForTiles">
            {tilefull.map((section, index) => (
              <Grid
                item
                xs={12}
                md={6}
                key={index}
                className="grid-item-for-content"
              >
                <Paper
                  className="paper-logo-predefined-content"
                  elevation={0}
                  key={index}
                  onClick={() => handleInputValue(section.title)}
                >
                  <img src={TileIcon} alt="tile-icon" className="tile-icon" />
                  <img
                    src={TileIconPink}
                    alt="tile-icon"
                    className="tile-icon-pink"
                  />
                  <p>{section.title}</p>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      <EmailDialog open={emailOpen} handleClose={handleClose} />
    </Box>
  );
}

export default memo(ChatsBox);
